// src/AuthContext.js

import React, { useEffect, useState, createContext } from 'react';
import { supabase } from './supabaseClient';
import { CircularProgress, Box } from '@mui/material';
import { processInvitationAPI } from './utils/invitationUtils';

export const AuthContext = createContext();

let isProcessingInvitation = false; // Module-level flag to prevent re-entrancy

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleProcessInvitation = async (session) => {
    if (!session || !session.access_token) {
      console.log('[AuthContext] handleProcessInvitation: No session or access token, exiting.');
      return;
    }

    const pendingToken = localStorage.getItem('pendingInvitationToken');
    console.log('[AuthContext] handleProcessInvitation: Checked localStorage for pendingInvitationToken. Found:', pendingToken);

    if (pendingToken) {
      if (isProcessingInvitation) {
        console.log('[AuthContext] Invitation processing already in progress for a token, skipping this call.');
        return;
      }
      isProcessingInvitation = true;

      console.log('[AuthContext] Session active and pending invitation token found. Processing invitation...');
      localStorage.removeItem('pendingInvitationToken'); 

      try {
        const result = await processInvitationAPI(pendingToken, session.access_token);
        if (result.success) {
          console.log('[AuthContext] Invitation processed successfully via AuthContext:', result.data);
          // Clear invitee flag if present to avoid onboarding mis-route after email confirmation
          try { localStorage.removeItem('isInviteeSignup'); } catch (_) {}
        } else {
          console.error('[AuthContext] Failed to process invitation via AuthContext:', result.error);
        }
      } catch (apiError) {
        console.error('[AuthContext] Critical error calling processInvitationAPI:', apiError);
      } finally {
        isProcessingInvitation = false; // Reset the flag
      }
    } else {
      console.log('[AuthContext] Session active, but NO pending invitation token found in localStorage.');
    }
  };

  const handleEmailVerification = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const type = urlParams.get('type');

    if (token && type === 'signup') {
      console.log('[AuthContext] Email verification token detected:', token);
      try {
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'email'
        });

        if (error) {
          console.error('[AuthContext] Email verification failed:', error);
        } else {
          console.log('[AuthContext] Email verification successful:', data);
          // Clear the URL parameters after successful verification
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (err) {
        console.error('[AuthContext] Error during email verification:', err);
      }
    }
  };

  useEffect(() => {
    const initSupabaseAndProcess = async () => {
      try {
        // First, handle email verification if token is present
        await handleEmailVerification();

        // During OAuth redirect, wait briefly for session to be ready to avoid redirecting to /login
        const searchParams = typeof window !== 'undefined' ? window.location.search : '';
        let { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('[AuthContext] Error getting initial session:', error);
        }
        if (!session && searchParams.includes('code=')) {
          // Poll for a short time for the session to be established
          for (let attempt = 1; attempt <= 10; attempt++) {
            await new Promise(r => setTimeout(r, 300));
            const { data: { session: s } } = await supabase.auth.getSession();
            if (s) { session = s; break; }
          }
        }

        setCurrentUser(session?.user ?? null);
        if (session) {
          await handleProcessInvitation(session);
        }
      } catch (err) {
        console.error('[AuthContext] Error initializing Supabase and processing:', err);
      } finally {
        setLoading(false);
      }
    };

    initSupabaseAndProcess();

    const { data: { subscription: authListener } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        console.log('[AuthContext] Auth state changed:', _event, 'User:', session?.user?.id);
        setCurrentUser(session?.user ?? null);

        if (_event === 'SIGNED_IN' && session) {
          await handleProcessInvitation(session);
        }
      }
    );

    return () => {
      authListener?.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const value = {
    currentUser,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};